/**
 * Public legal page (/legal/imprint, /legal/privacy-policy, /legal/terms-of-service).
 *
 * Reads from public.legal_pages — one row per slug with EN/DE columns,
 * seeded by the 20260506120000_legal_pages migration. The previous
 * implementation called the sync-storyblok edge function on every load
 * to fetch content from Storyblok CMS, with a long inline hardcoded
 * fallback that doubled as the source of truth in offline / Storyblok-
 * down scenarios. The new flow is a single PostgREST query — no edge
 * function, no Storyblok dependency.
 *
 * Fallback: if the DB read fails or the slug doesn't exist, we render
 * a generic "couldn't load this page" message with a contact link
 * rather than attempting a static fallback. The seed migration covers
 * every slug we serve today; adding a new legal page = one INSERT.
 */
import { useParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import "../styles/legal-content.css";

type LegalPageRow = {
  slug: string;
  title_en: string | null;
  title_de: string | null;
  content_en: string | null;
  content_de: string | null;
  meta_description_en: string | null;
  meta_description_de: string | null;
};

type LegalPageView = {
  slug: string;
  title: string;
  content: string;
};

async function fetchLegalPage(
  slug: string,
  language: "en" | "de"
): Promise<LegalPageView | null> {
  const { data, error } = await supabase
    .from("legal_pages")
    .select(
      "slug, title_en, title_de, content_en, content_de, meta_description_en, meta_description_de"
    )
    .eq("slug", slug)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const row = data as LegalPageRow;
  // Project to the active language. Fall back to the other language if
  // the active one is missing — same shape as LanguageContext's t()
  // fallback. Nullable columns mean a row can exist with only EN or DE.
  const title =
    (language === "en" ? row.title_en : row.title_de) ??
    (language === "en" ? row.title_de : row.title_en) ??
    "";
  const content =
    (language === "en" ? row.content_en : row.content_de) ??
    (language === "en" ? row.content_de : row.content_en) ??
    "";
  return { slug: row.slug, title, content };
}

const LegalPage = () => {
  const params = useParams();
  const location = useLocation();
  // Routes are flat (/imprint, /privacy-policy, /terms-of-service) so
  // params['*'] is undefined; fall back to the last path segment.
  const slug =
    params["*"] || location.pathname.split("/").filter(Boolean).pop() || "";
  const { t, language } = useLanguage();

  const {
    data: legalPage,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["legal-page", slug, language],
    queryFn: () => fetchLegalPage(slug, language as "en" | "de"),
    enabled: !!slug,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 pb-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gray-200 animate-pulse h-28" />
            <div className="px-8 py-10 md:px-12 md:py-12 space-y-6">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-5 w-1/2 mt-8" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !legalPage || !legalPage.content) {
    return (
      <div className="min-h-screen pt-24 pb-20">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto">
            <CardContent className="p-8">
              <h1 className="text-2xl font-bold mb-4">
                {t("error.pageNotFound") || "Page not found"}
              </h1>
              <p className="text-muted-foreground">
                {t("error.pageNotFoundDesc") ||
                  "We couldn't load this page. Please try again or contact us."}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 bg-gray-50">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-900 to-gray-700 px-8 py-10 md:px-12 md:py-12">
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              {legalPage.title}
            </h1>
          </div>
          <div className="px-8 py-10 md:px-12 md:py-12">
            <div
              className="legal-content prose prose-gray max-w-none"
              dangerouslySetInnerHTML={{
                __html: formatLegalContent(legalPage.content),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Normalize the stored content for rendering.
 *   - if it already contains HTML tags, render as-is
 *   - otherwise treat it as markdown-like and convert headings + paragraphs
 *
 * Most legal_pages rows store full HTML (the seed migration uses HTML),
 * but the markdown path is kept so an admin editing via SQL can paste
 * plain text and have it render reasonably.
 */
function formatLegalContent(content: string): string {
  if (content.includes("<")) return content;
  return content
    .replace(/^# (.*$)/gim, "<h1>$1</h1>")
    .replace(/^## (.*$)/gim, "<h2>$1</h2>")
    .replace(/^### (.*$)/gim, "<h3>$1</h3>")
    .replace(/\n\n/g, "</p><p>")
    .replace(/^(.+)$/gim, "<p>$1</p>")
    .replace(/<p><h/g, "<h")
    .replace(/<\/h([1-6])><\/p>/g, "</h$1>");
}

export default LegalPage;
